import Phaser from "phaser";
import type { GameContext } from "../GameContext";
import { CharacterSelectController } from "../controllers/CharacterSelectController";
import type { CharacterSummary } from "../controllers/CharacterSelectController";
import { WorldController } from "../controllers/WorldController";

/**
 * Per 1.5: "First login -> Character Select, empty, prompted to create a
 * character" and "Character Creation -> name, starting appearance,
 * confirm. No job selection yet — every character starts as a Wanderer
 * (Tier 0)." Per 1.5's next step: "First 'Enter World' -> spawns in
 * Aurelia Spire (always the entry point for a new character)".
 */
export class CharacterSelectScene extends Phaser.Scene {
  private listContainer!: HTMLDivElement;
  private statusText!: Phaser.GameObjects.Text;

  constructor() {
    super("CharacterSelectScene");
  }

  private get ctx(): GameContext {
    return this.game.registry.get("ctx") as GameContext;
  }

  async create(): Promise<void> {
    const { width } = this.cameras.main;

    this.load.svg("logo-icon-small", "/art/logo/aetheria-logo-icon.svg", { width: 36, height: 36 });
    this.load.once("complete", () => {
      this.add.image(width / 2 - 140, 60, "logo-icon-small").setOrigin(0.5);
    });
    this.load.start();

    this.add
      .text(width / 2, 60, "Choose Your Wanderer", { fontFamily: "Baloo 2, sans-serif", fontSize: "26px", color: "#f5a93f" })
      .setOrigin(0.5);
    this.statusText = this.add
      .text(width / 2, 650, "", { fontFamily: "Inter, sans-serif", fontSize: "13px", color: "#e85d4e" })
      .setOrigin(0.5);

    this.listContainer = document.createElement("div");
    this.add.dom(width / 2, 140, this.listContainer).setOrigin(0.5, 0);

    await this.refreshList();
  }

  private async refreshList(): Promise<void> {
    const controller = new CharacterSelectController(this.ctx);
    const result = await controller.listCharacters();

    if (!result.ok) {
      this.statusText.setText(result.message);
      return;
    }

    this.renderList(result.characters);
  }

  private renderList(characters: CharacterSummary[]): void {
    this.listContainer.innerHTML = "";

    if (characters.length === 0) {
      const emptyMsg = document.createElement("p");
      emptyMsg.textContent = "No Wanderers yet. Create your first one below.";
      this.listContainer.appendChild(emptyMsg);
    }

    for (const character of characters) {
      const row = document.createElement("div");
      row.className = "character-row";
      row.innerHTML = `
        <span>${escapeHtml(character.name)} — ${escapeHtml(character.jobId)} (Lv. ${character.baseLevel})</span>
        <button data-enter="${character.id}">Enter World</button>
      `;
      this.listContainer.appendChild(row);
    }

    const createRow = document.createElement("div");
    createRow.innerHTML = `
      <input data-new-name placeholder="New character name" />
      <button data-create>Create Character</button>
    `;
    this.listContainer.appendChild(createRow);

    this.listContainer.querySelectorAll<HTMLButtonElement>("[data-enter]").forEach((btn) => {
      btn.addEventListener("click", () => void this.enterWorld(btn.dataset["enter"]!));
    });
    this.listContainer.querySelector<HTMLButtonElement>("[data-create]")?.addEventListener("click", () => {
      void this.createCharacter();
    });
  }

  private async createCharacter(): Promise<void> {
    const nameInput = this.listContainer.querySelector<HTMLInputElement>("[data-new-name]");
    const name = nameInput?.value.trim();
    if (!name) {
      this.statusText.setText("Enter a name for your new Wanderer.");
      return;
    }

    // Per 1.4/1.5: every new character starts as Wanderer (Tier 0), no job
    // selection at creation time.
    const result = await this.ctx.network.rest.post<CharacterSummary>("/api/v1/characters", { name });
    if (!result.ok) {
      this.statusText.setText(result.message);
      return;
    }
    this.statusText.setText("");
    await this.refreshList();
  }

  private async enterWorld(characterId: string): Promise<void> {
    this.statusText.setText("");
    const selectController = new CharacterSelectController(this.ctx);
    const result = await selectController.selectCharacter(characterId);

    if (!result.ok) {
      this.statusText.setText(result.message);
      return;
    }

    const worldController = new WorldController(this.ctx);
    const unsubscribe = this.ctx.network.on("ws:open", () => {
      unsubscribe();
      this.scene.start("WorldScene", { character: result.character });
    });
    worldController.attachAndConnect(result.websocketUrl);
  }
}

function escapeHtml(value: string): string {
  const div = document.createElement("div");
  div.textContent = value;
  return div.innerHTML;
}
